import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { staffApi } from "./staffApi";
import { Paginated, StaffUserRow } from "../types/dtos";

export type UserListParams = { page?: number; pageSize?: number; q?: string };

export function useUsers(params: UserListParams): UseQueryResult<Paginated<StaffUserRow>> {
  return useQuery({
    queryKey: ["users", params],
    queryFn: async () => (await staffApi.get<Paginated<StaffUserRow>>("/users", { params })).data,
    placeholderData: (prev: Paginated<StaffUserRow> | undefined) => prev,
  });
}

export type CreateUserInput = { name: string; email: string; role: StaffUserRow["role"]; password: string };

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => staffApi.post("/users", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<Pick<StaffUserRow, "name" | "email" | "role" | "active">> }) =>
      staffApi.patch(`/users/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      staffApi.post(`/users/${id}/reset-password`, { password }),
  });
}
